import PathConstants from '@/main/routes/paths'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import logoIconUnicoSkill from '@images/logo-icon-unico-skill.svg'
import { AppBar, Box, Container, Toolbar, useScrollTrigger, useTheme } from '@mui/material'
import { Link } from 'react-router-dom'
import SxHeader from '../sx-header'
import UserChangeLanguageForm from '@/presentation/pages/user-settings/ui/user-change-language-form'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'

type BasicHeaderProps = {
  targetScroll?: Node | undefined
  children?: ReactNode
}

export default function BasicHeader({ children, targetScroll }: BasicHeaderProps) {
  const theme = useTheme()
  const [account] = useAccount() as ValidAccountState
  const [trigger, setTrigger] = useState(false)
  const [target, setTarget] = useState(targetScroll)
  const { HOME } = PathConstants.Routes

  const handleClickOnLogo = () => {
    AnalyticsTracker.trackEvent(AnalyticsEventName.HEADER_NAVIGATE, {
      destination: HOME.name
    })
  }

  const newTrigger = useScrollTrigger({
    target: target || undefined
  })

  useEffect(() => {
    setTarget(targetScroll)
  }, [targetScroll])

  if (newTrigger != trigger) {
    setTrigger(newTrigger)
  }

  // Calculate header height for spacing
  // Desktop: 64px (main)
  // Mobile: 64px (main only)
  const desktopHeaderHeight = theme.spacing(8)
  const mobileHeaderHeight = theme.spacing(8)

  return (
    <Box id="basic-header" component="header" className="w-screen relative z-[100]">
      <AppBar component="nav" sx={SxHeader.appBar(false, true)}>
        <Toolbar
          sx={{
            height: theme.spacing(8),
            bgcolor: theme.palette.common.white
          }}
          className="border-0 border-b border-solid border-[color:var(--grey-50)]"
        >
          <Container
            disableGutters
            maxWidth="lg"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flex: 1,
                alignItems: 'center'
              }}
            >
              <Box
                component={Link}
                to={HOME.value}
                onClick={handleClickOnLogo}
                sx={{
                  height: theme.spacing(5),
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <img src={logoIconUnicoSkill} alt="Unico Skill" />
                <img
                  src={`${account.company.logoUrl}`}
                  alt={account.company.name}
                  className="h-[70%] ml-2"
                />
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '480px',
                margin: '0 auto'
              }}
            >
              {children}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flex: 1,
                alignItems: 'center'
              }}
            >
              <UserChangeLanguageForm submitOnChange={true} />
            </Box>
          </Container>
        </Toolbar>
      </AppBar>

      {/* Spacer to prevent content from going under fixed header */}
      <Box
        sx={{
          height: {
            xs: mobileHeaderHeight,
            md: desktopHeaderHeight
          }
        }}
      />
    </Box>
  )
}
